import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatearFecha, fechaParaInput } from '../utils/fecha';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Un tono por serie, de la paleta validada para daltonismo sobre fondo blanco
const AZUL = '#2a78d6';    // cobros
const NARANJA = '#eb6834'; // gastos
const pesos = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

const mesLegible = (clave) => {
  if (!clave) return '';
  const [anio, mes] = String(clave).split('-');
  const n = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${n[parseInt(mes, 10) - 1] || ''} ${anio}`;
};

function Expenses() {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(fechaParaInput(new Date()));
  const [expenses, setExpenses] = useState([]);
  const [analisis, setAnalisis] = useState(null);

  const categories = [
    '💰 Préstamo',
    '🏦 Banco',
    '💡 Servicios Públicos',
    '👩‍💼 Empleada Doméstica',
    '🛒 Compras',
    '🍔 Alimentación',
    '🏥 Salud',
    '📚 Educación',
    '🚗 Transporte',
    '📱 Comunicación',
    '🏠 Arriendo',
    'Otro'
  ];

  useEffect(() => {
    loadExpenses();
    cargarAnalisis();
  }, []);

  const cargarAnalisis = () => {
    axios.get('/api/expenses/analisis')
      .then(res => setAnalisis(res.data))
      .catch(err => console.error('Error cargando el análisis:', err));
  };

  const loadExpenses = async () => {
    try {
      const res = await axios.get('/api/expenses');
      setExpenses(res.data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const borrarGasto = async (gasto) => {
    const ok = window.confirm(
      `¿Borrar "${gasto.description}" de ${pesos(gasto.amount)}?\n\n` +
      'Volverán a calcularse la media y las alertas sin este gasto.'
    );
    if (!ok) return;

    try {
      await axios.delete(`/api/expenses/${gasto.id}`);
      loadExpenses();
      cargarAnalisis();
    } catch (err) {
      alert('No se pudo borrar el gasto');
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!description || !amount || !category) {
      alert('Completa todos los campos');
      return;
    }

    try {
      await axios.post('/api/expenses', {
        description,
        amount: parseFloat(amount),
        category,
        date
      });
      setDescription('');
      setAmount('');
      setCategory('');
      alert('✅ Gasto registrado');
      loadExpenses();
      cargarAnalisis();
    } catch (err) {
      alert('❌ Error: ' + err.message);
    }
  };

  return (
    <div className="expenses-page">
      <h2>💸 Registro de Gastos</h2>

      {/* Veredicto del mes. El color y la palabra van juntos: quien no
          distinga bien los tonos lee igual el diagnóstico. */}
      {analisis && (() => {
        const d = analisis.desviacion;
        const hayAlertas = analisis.alertas.length > 0;

        // Una categoría disparada manda sobre el total. Sin esto la tarjeta
        // decía «11% por debajo de tu media» en verde mientras justo debajo
        // avisaba de un +700% en transporte: el resumen tranquilizaba y el
        // detalle alarmaba, y quien solo mira el titular no se enteraba.
        const alto = (d !== null && d >= 25) || hayAlertas;
        const bajo = !hayAlertas && d !== null && d <= -15;
        const fondo = alto ? '#fef2f2' : bajo ? '#f0fdf4' : '#f9fafb';
        const borde = alto ? '#fecaca' : bajo ? '#bbf7d0' : '#e5e7eb';
        const tinta = alto ? '#991b1b' : bajo ? '#166534' : '#374151';

        return (
          <div style={{
            background: fondo, border: `1px solid ${borde}`, borderRadius: '12px',
            padding: '22px', marginBottom: '20px'
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>Gastos de este mes</div>
            <div style={{ fontSize: '38px', fontWeight: 'bold', color: tinta, lineHeight: 1.2 }}>
              {pesos(analisis.total_actual)}
            </div>

            {!analisis.historial_suficiente ? (
              <div style={{ color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
                Llevas {analisis.meses_de_historial}{' '}
                {analisis.meses_de_historial === 1 ? 'mes registrado' : 'meses registrados'}.
                A partir de tres podré comparar y avisarte de desviaciones.
              </div>
            ) : (
              <div style={{ color: tinta, fontSize: '15px', marginTop: '8px' }}>
                {analisis.total_actual === 0
                  ? 'Todavía no has registrado gastos este mes.'
                  : d > 0
                    ? `${d}% por encima de tu media (${pesos(analisis.media_anterior)})`
                    : d < 0
                      ? `${Math.abs(d)}% por debajo de tu media (${pesos(analisis.media_anterior)})`
                      : `En línea con tu media (${pesos(analisis.media_anterior)})`}
                {/* La comparación es > 0, no a secas: con la proyección en
                    cero JSX imprimía un «0» suelto detrás del texto. */}
                {analisis.proyeccion > 0 && (
                  <span style={{ color: '#6b7280' }}>
                    {' · '}al ritmo actual cerrarías en {pesos(analisis.proyeccion)}
                  </span>
                )}
                {hayAlertas && d < 25 && (
                  <div style={{ marginTop: '6px', fontWeight: 600 }}>
                    Aun así, revisa: hay {analisis.alertas.length === 1
                      ? 'una categoría disparada'
                      : `${analisis.alertas.length} categorías disparadas`}.
                  </div>
                )}
              </div>
            )}

            {/* Lo que de verdad importa: si entró más de lo que salió */}
            <div style={{
              display: 'flex', gap: '24px', marginTop: '16px', paddingTop: '14px',
              borderTop: `1px solid ${borde}`, flexWrap: 'wrap'
            }}>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>Cobrado este mes</div>
                <strong style={{ color: '#166534' }}>{pesos(analisis.cobrado_actual)}</strong>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>Diferencia</div>
                <strong style={{ color: analisis.balance >= 0 ? '#166534' : '#991b1b' }}>
                  {analisis.balance >= 0 ? '+' : ''}{pesos(analisis.balance)}
                </strong>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Alertas por categoría */}
      {analisis && analisis.alertas.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          {analisis.alertas.map(a => (
            <div key={a.categoria} style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
              padding: '14px', marginBottom: '10px', fontSize: '14px', color: '#92400e'
            }}>
              ⚠️ <strong>{a.categoria}</strong> lleva {pesos(a.actual)} este mes,
              cuando sueles gastar {pesos(a.media)}.
              Son <strong>{pesos(a.diferencia)} de más</strong> ({a.subida}%).
            </div>
          ))}
        </div>
      )}

      {/* Tendencia: dos series, así que llevan leyenda */}
      {analisis && analisis.serie.length > 1 && (
        <div style={{
          background: 'white', borderRadius: '10px', padding: '20px',
          border: '1px solid #e5e7eb', marginBottom: '20px'
        }}>
          {/* Trazo recto, no suavizado: la curva 'monotone' dibujaba una
              subida progresiva entre marzo y abril cuando el cobro pasó de
              cero a 1.800.000 de golpe. Son valores mensuales sueltos, no una
              magnitud continua. */}
          <h3 style={{ marginTop: 0, fontSize: '16px', color: '#374151' }}>
            Gastos y cobros por mes
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={analisis.serie.map(m => ({
              mes: mesLegible(m.mes), Gastos: m.gastos, Cobros: m.cobros
            }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="mes" tick={{ fill: '#6b7280', fontSize: 12 }} tickLine={false} />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }} axisLine={false} tickLine={false} width={70}
                tickFormatter={(v) => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`)}
              />
              <Tooltip
                formatter={(v) => pesos(v)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
              />
              <Legend />
              <Area type="linear" dataKey="Cobros" stroke={AZUL} strokeWidth={2} fill={AZUL} fillOpacity={0.12} />
              <Area type="linear" dataKey="Gastos" stroke={NARANJA} strokeWidth={2} fill={NARANJA} fillOpacity={0.12} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="form-card">
        <h3>Agregar Gasto</h3>
        <form onSubmit={handleAddExpense}>
          <div className="form-group">
            <label>Descripción *</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Pago de agua"
              required
            />
          </div>

          <div className="form-group">
            <label>Categoría *</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} required>
              <option value="">-- Selecciona categoría --</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', gap: '10px' }}>
            <div style={{ flex: 1 }}>
              <label>Monto ($) *</label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Fecha *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
          </div>

          <button type="submit" className="btn-primary">➕ Registrar Gasto</button>
        </form>
      </div>

      <div className="table-container">
        <h3>📋 Últimos Gastos</h3>
        {expenses.length === 0 ? (
          <p className="empty-state">No hay gastos registrados</p>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Categoría</th>
                <th>Descripción</th>
                <th>Monto</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 30).map((exp, idx) => (
                <tr key={exp.id}>
                  <td>{idx + 1}</td>
                  <td>{exp.category}</td>
                  <td>{exp.description}</td>
                  <td><strong>{pesos(exp.amount)}</strong></td>
                  <td>{formatearFecha(exp.date)}</td>
                  <td>
                    <button
                      onClick={() => borrarGasto(exp)}
                      title="Borrar este gasto"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '16px', padding: '4px'
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Expenses;
